import { useContext, useEffect, ReactElement, useReducer, useState } from 'react';

import { useAuthState } from '../../../providers/authProvider';
import { UserDetailContext, UserDetailContextProps } from '../../../context/userDetail/userDetailContext';
import { SearchDetailContext, SearchDetailContextProps } from '../../../context/searchDetail/searchDetailContext';
import { ChooseSite } from './ChooseSite';
import { GridFilter } from '../../gridfilter/GridFilter';
import { gridReducer, GRID_ACTIONS, IGridReducerState } from '../../../reducer/gridReducer/gridReducer';
import { isOpusBrand } from '../../../utils/common';
import { RoutingToTypes } from '../../search/SearchActions/SearchActions';
import { EssAccountsTypes } from '../../../types/account';
import { FilterRowWrapper } from '../../../common/components';
import { getSelectedCustomerId } from '../../../helpers/customer';
import { getInitialType } from '../../../utils/getInitialTypes';
import { getMeteringPoints, getMeters, MetersFilterObject } from '../../../common/api/meterReadsApi';
import { globalApiParams } from '../../../common/api/globals';
import { formatSites } from '../../meterreads/helper';
import * as colors from '../../../common/styles/colors';
import { MeterUpgradeChooseSiteWrapper } from './MeterUpgradeContactDetails.styles';
import { MeterUpgradeProcessContext } from '../meterUpradeProcess/meterUpgradeProcessContext';
import { getHealthCheck } from '../../../common/api/healthCheckApi';

type MeterUpgradeChooseSiteProps = {
  stepsBar: ReactElement;
  pageTitle: ReactElement;
};

export const MeterUpgradeChooseSite = ({ stepsBar, pageTitle }: MeterUpgradeChooseSiteProps) => {
  const authContext = useAuthState();
  const { setRawGridData } = useContext<any>(MeterUpgradeProcessContext);
  const { userDetail, setUserDetail } = useContext<UserDetailContextProps>(UserDetailContext);
  const searchContext = useContext<SearchDetailContextProps>(SearchDetailContext);
  const [customerAccountsFromHealthCheck, setCustomerAccountsFromHealthCheck] = useState<Array<Common.IOption>>();

  const initialState: IGridReducerState = {
    page: 1,
    currentType: '',
    data: {},
    initialSortField: 'site-reference',
    initialSortOrder: 'DESC',
    currentSortField: 'site-reference',
    currentSortOrder: 'DESC',
    isLoading: false,
    hasMoreData: true,
    selectedCustomerId: getSelectedCustomerId(userDetail),
  };

  const [state, dispatch] = useReducer(gridReducer, initialState);

  const mhhsLink = 'https://energy.drax.com/market-wide-half-hourly-settlement/';
  const smartLink = 'https://energy.drax.com/smart-meters/benefits-of-smart-meters/';

  // When navigating directly to url
  useEffect(() => {
    if (userDetail.essCustomerId) {
      initialState.currentType = getInitialType(userDetail.hasElectricity, userDetail.hasGas, userDetail.hasRenewable);
      dispatch({ type: GRID_ACTIONS.REINIT, payload: initialState });
    }
    // TODO:  Might want to remove this and revisit this problem later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetail.hasElectricity, userDetail.hasGas, userDetail.hasRenewable]);

  useEffect(() => {
    if (!state.selectedCustomerId) return;

    setUserDetail({
      ...userDetail,
      essCustomerId: state.selectedCustomerId,
    });

    loadSitesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedCustomerId]);

  const loadSitesData = async () => {
    const sites = await getMeteringPoints(
      process.env.REACT_APP_API_URL,
      authContext,
      state.selectedCustomerId,
      globalApiParams.maxFilterPageSize,
      state.page,
      'Active',
      undefined
    );

    const payload = {
      essCustomerId: state.selectedCustomerId,
      data: {
        customerFilters: [],
        accountFilters: [],
        sitesFilters: formatSites(sites),
        mpansFilters: [],
      },
    };

    dispatch({ type: GRID_ACTIONS.INIT_FILTERS, payload });
  };

  const clearData = () => {
    dispatch({ type: GRID_ACTIONS.CLEAR_DATA });
  };

  const loadData = async (customerId: string) => {
    dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: true });

    const filters: MetersFilterObject = {
      'meter-type': 'N',
    };
    let uniqueData: any[] = [];

    let rawData: Array<Api.IMeter> = await getMeters(
      process.env.REACT_APP_API_URL,
      authContext,
      customerId,
      filters,
      state.page,
      undefined,
      undefined,
      'Active'
    );

    if (rawData) {
      setRawGridData(rawData);
      uniqueData = rawData.filter(
        (value, index, self) => index === self.findIndex(t => t['site-reference'] === value['site-reference'])
      );
      dispatch({ type: GRID_ACTIONS.ADD_DATA, payload: { data: rawData, uniqueData: uniqueData, page: state.page } });
    } else {
      clearData();
    }
    dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });
  };

  useEffect(() => {
    if (searchContext.searchDetail != null && searchContext.searchDetail.searchText.length > 0) {
      // filter by search result

      if (searchContext.searchDetail.type && searchContext.searchDetail.routingTo === RoutingToTypes.meterUpgrade) {
        const type = searchContext.searchDetail.type.toLowerCase();
        if (type === 'account') {
          dispatch({ type: GRID_ACTIONS.CHANGE_TYPE, payload: EssAccountsTypes.Electricity });
          dispatch({
            type: GRID_ACTIONS.FILTER_BY_CUSTOMER,
            payload: { essCustomerId: searchContext.searchDetail.searchText },
          });
        }
        // Clear Search Detail
        searchContext.setSearchDetail({ searchText: '' });
      }
    }
    // TODO:  Might want to remove this and revisit this problem later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchContext.searchDetail.type]);

  useEffect(() => {
    // Ignore if we are entering component from Search
    if (
      searchContext.searchDetail != null &&
      searchContext.searchDetail.type &&
      searchContext.searchDetail.type.length > 0
    )
      return;

    // Don't load if no customer selected
    if (!state.selectedCustomerId) return;

    loadData(state.selectedCustomerId);
    // TODO: Might want to remove this and revisit this problem later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.selectedCustomerId,
    state.currentType,
    state.page,
    state.selectedEssAccountId,
    state.currentFilter,
    state.currentSortField,
    state.currentSortOrder,
  ]);

  const handleCheckedRow = (site: any) => {
    //dispatch({ type: GRID_ACTIONS.CHANGE_SORT, payload: sort });
  };

  const handleCustomerFilterChange = (option: Common.IOption) => {
    dispatch({ type: GRID_ACTIONS.FILTER_BY_CUSTOMER, payload: { essCustomerId: option.value } });
  };

  const handleAccountFilterChange = (option: Common.IOption) => {
    const essAccountId = option.value;
    dispatch({ type: GRID_ACTIONS.FILTER_BY_ACCOUNT, payload: { essAccountId: essAccountId } });
  };

  const handleSiteFilterChange = (option: Common.IOption) => {
    const essSiteId = option ? option.value : '';
    dispatch({ type: GRID_ACTIONS.FILTER_BY_SITE, payload: { essSiteId: essSiteId } });
  };

  const handleClearAll = () => {
    dispatch({ type: GRID_ACTIONS.CLEAR_FILTERS });
  };

  const handlePagingChange = () => {
    dispatch({ type: GRID_ACTIONS.INCREMENT_PAGE });
  };

  useEffect(() => {
    const load = async () => {
      const accounts = await getCustomersFromHealthCheck();
      setCustomerAccountsFromHealthCheck(accounts);
    };
    load();
  }, []);

  const getCustomersFromHealthCheck = async (): Promise<Array<Common.IOption>> => {
    const accounts = userDetail.customerAccounts.map((account: Api.IEssAccountData) => {
      return account.essAccountID;
    });
    const apiMeterUpgrade = await getHealthCheck(
      process.env.REACT_APP_API_URL,
      authContext,
      '/ersgatewayadapter/meter-health',
      'MeterUpgrade',
      accounts
    );

    if (apiMeterUpgrade.success) {
      const uniqueAccounts = Array.from(
        new Set(apiMeterUpgrade.data.response?.map((item: any) => item['customer-account-reference']))
      );

      const accounts: Array<Common.IOption> = uniqueAccounts.map((account: string) => {
        const customerAccount = userDetail.customerAccounts.find(item => item.essAccountID === account);

        if (!customerAccount) {
          return {
            value: account,
            display: account,
          };
        }

        return {
          value: account,
          display: customerAccount.accountName,
        };
      });

      return accounts;
    }

    return [];
  };

  return (
    <MeterUpgradeChooseSiteWrapper>
      <FilterRowWrapper>{pageTitle}</FilterRowWrapper>

      <GridFilter
        showSites={false}
        customerData={state.data?.customers}
        accountData={customerAccountsFromHealthCheck}
        siteData={state.data?.sites}
        selectedType={state.currentType}
        handleCustomerFilterChange={handleCustomerFilterChange}
        handleAccountFilterChange={handleAccountFilterChange}
        handleSiteFilterChange={handleSiteFilterChange}
        handleClearAllClick={handleClearAll}
        showClearAll={true}
        showAccount={isOpusBrand}
        selectAccountsOverride={true}
      />
      {state.selectedCustomerId && <div className='pt-3 pl-4 pl-lg-0'>{stepsBar}</div>}

      <div className='px-4 pb-3 pageText'>
        <p>
          Our records show that your business has meters that are incompatible with{' '}
          <a style={{ color: colors.hydroBlue }} href={mhhsLink} target='_blank'>
            MHHS
          </a>
          . If you want to take advantage of the valuable benefits{' '}
          <a style={{ color: colors.hydroBlue }} href={mhhsLink} target='_blank'>
            MHHS
          </a>{' '}
          will bring, you need to upgrade these to a{' '}
          <a style={{ color: colors.hydroBlue }} href={smartLink} target='_blank'>
            Smart meter
          </a>
          .
        </p>
        <p>
          Without this upgrade, your outdated meters may cause significant issues, including estimated readings, which
          could provide an inaccurate picture of your electricity consumption.
        </p>
        <p>
          Please complete the following form to allow us to support you in this transition and ensure your meters are up
          to date.
        </p>
        <p>
          <i>If you have already completed this form you do not need to do so again</i>
        </p>
      </div>

      <ChooseSite
        selectedType={state.currentType}
        handleCheckedRow={handleCheckedRow}
        handlePagingChange={handlePagingChange}
        gridData={state.gridData}
        isLoading={state.isLoading}
        currentSortField={state.currentSortField}
        currentSortOrder={state.currentSortOrder}
        hasMoreData={state.hasMoreData}
      />
    </MeterUpgradeChooseSiteWrapper>
  );
};
